import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  CircularProgress,
  styled,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { IoCubeOutline, IoSettingsOutline } from "react-icons/io5";
import { toast } from "react-toastify";
import { getApiData, postFormData } from "../../../../utils/APIHelper";
import AddItemModal from "./AddItemModal";
import DeleteBoqModal from "./DeleteBoqModal";
import DeleteInclusionModal from "./DeleteInclusionModal";
import {
  getRoomsBOQs,
  getInclusionsBOQs,
  getAvailableInclusions,
  getAvailableInclusionsForRoom,
  addInclusionToRoom,
  addBOQToInclusion,
  deleteBOQ,
  updateBOQ,
  deleteInclusion,
} from "../../../../services/budgetService";
import styles from "./styles";
import {
  ByRoomTab,
  ByCategoryTab,
} from "../../../../components/ProjectLevelBOQ/variants/roomBOQ";
import {
  renderCurrencyIconColored,
  getCurrentCurrencyLocale,
} from "../../../../utils/CurrencyUtils";
import { formattedAmountWithoutAed } from "../../../../utils/CommonFunction";

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 500,
  fontSize: "14px",
  minHeight: "48px",
  display: "flex",
  flexDirection: "row",
  gap: 8,
  "&.Mui-selected": {
    color: "#4D6BF6",
  },
  "& .MuiTab-iconWrapper": {
    marginBottom: 0,
    marginRight: 8,
  },
}));

const formatAed = (val) => {
  const n = Number(val ?? 0);
  if (Number.isNaN(n)) return `${getCurrentCurrencyLocale()} 0`;
  return `${getCurrentCurrencyLocale()} ${n.toLocaleString("en-US")}`;
};

const computeBoqSummary = (roomsPayload) => {
  if (!roomsPayload) return null;
  const roomList = Array.isArray(roomsPayload) ? roomsPayload : [];
  let totalBoqs = 0;
  let coveredBoqs = 0;
  let totalBoqCost = 0;
  for (const room of roomList) {
    for (const inc of room.inclusions || []) {
      for (const boq of inc.boqs || []) {
        totalBoqs += 1;
        const cost = Number(boq.total_cost);
        if (cost !== 0 && !Number.isNaN(cost)) {
          coveredBoqs += 1;
          totalBoqCost += cost;
        }
      }
    }
  }
  return { totalBoqs, coveredBoqs, totalBoqCost };
};

const Budgets = ({ details, isMobile: isMobileProp }) => {
  const { projectId } = useParams();
  const theme = useTheme();
  const isMobileQuery = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isMobile = isMobileProp ?? isMobileQuery;
  const budgetStyles = styles(isMobile);
  const { userData } = useSelector((state) => state.auth);
  const hideUnitPrice = !!userData?.contractor_data;

  const [activeTab, setActiveTab] = useState(0);

  const [rooms, setRooms] = useState([]);
  const [inclusions, setInclusions] = useState([]);
  const [availableInclusions, setAvailableInclusions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
  const [selectedInclusionForItem, setSelectedInclusionForItem] =
    useState(null);
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [addingInclusion, setAddingInclusion] = useState(null);
  const [isEditingItem, setIsEditingItem] = useState(false);
  const [editingBoq, setEditingBoq] = useState(null);
  const [isDeleteBoqModalOpen, setIsDeleteBoqModalOpen] = useState(false);
  const [boqToDelete, setBoqToDelete] = useState(null);
  const [isDeleteInclusionModalOpen, setIsDeleteInclusionModalOpen] =
    useState(false);
  const [inclusionToDelete, setInclusionToDelete] = useState(null);
  const [selectedRoomForModal, setSelectedRoomForModal] = useState(null);
  const [inclusionIdForModal, setInclusionIdForModal] = useState(null);
  const [inlineEditingBoqId, setInlineEditingBoqId] = useState(null);
  const [inlineEditingBoqInclusion, setInlineEditingBoqInclusion] = useState(null);

  const boqSummary = useMemo(() => computeBoqSummary(rooms), [rooms]);

  useEffect(() => {
    if (details?.id) {
      loadBudgetData();
    }
  }, [details?.id]);

  const loadBudgetData = async () => {
    try {
      setLoading(true);
      const [roomsResponse, inclusionsResponse, availableInclusionsResponse] =
        await Promise.all([
          getRoomsBOQs(details.id),
          getInclusionsBOQs(details.id),
          getAvailableInclusions(details.project_type || "Villa"),
        ]);

      if (roomsResponse.success) {
        setRooms(roomsResponse.rooms || []);
      }

      if (inclusionsResponse.success) {
        setInclusions(inclusionsResponse.inclusions || []);
      }

      if (availableInclusionsResponse.success) {
        setAvailableInclusions(availableInclusionsResponse.inclusions || []);
      }
    } catch (error) {
      console.error("Error loading budget data:", error);
      toast.error("Failed to load budget data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddItemModal = (inclusion, room = null) => {
    setSelectedInclusionForItem(inclusion);
    setIsEditingItem(false);
    setEditingBoq(null);

    if (activeTab === 0 && room) {
      setSelectedRoomForModal({
        id: room.room_id,
        name: room.room_name,
      });
      setInclusionIdForModal(inclusion.id);
    } else {
      setSelectedRoomForModal(null);
      setInclusionIdForModal(inclusion.id || null);
    }

    setIsAddItemModalOpen(true);
  };

  const handleCloseAddItemModal = () => {
    setIsAddItemModalOpen(false);
    setSelectedInclusionForItem(null);
    setIsEditingItem(false);
    setEditingBoq(null);
    setSelectedRoomForModal(null);
    setInclusionIdForModal(null);
  };

  const handleRoomChange = (selectedRoom) => {
    setSelectedRoomForModal(selectedRoom);

    if (activeTab === 1 && selectedInclusionForItem && selectedRoom) {
      const selectedRoomId =
        selectedRoom.id || selectedRoom.value || selectedRoom.room_id;

      const roomInInclusion = selectedInclusionForItem.rooms?.find(
        (r) => (r.id || r.room_id) === selectedRoomId
      );

      if (roomInInclusion?.inclusion_id) {
        setInclusionIdForModal(roomInInclusion.inclusion_id);
      } else if (selectedInclusionForItem.id) {
        setInclusionIdForModal(selectedInclusionForItem.id);
      } else {
        setInclusionIdForModal(null);
      }
    }
  };

  const handleEditBoq = (boq, inclusion) => {
    if (isMobile) {
      setEditingBoq(boq);
      setSelectedInclusionForItem(inclusion);
      setIsEditingItem(true);
      setIsAddItemModalOpen(true);
    } else {
      setInlineEditingBoqId(boq?.id);
      setInlineEditingBoqInclusion(inclusion);
    }
  };

  const handleAfterInlineEditSave = async () => {
    setInlineEditingBoqId(null);
    setInlineEditingBoqInclusion(null);
    await loadBudgetData();
  };

  const handleCancelInlineEditBoq = () => {
    setInlineEditingBoqId(null);
    setInlineEditingBoqInclusion(null);
  };

  const fetchAvailableInclusionsForRoom = async (roomName) => {
    if (!details?.project_type || !roomName) return null;
    try {
      return await getAvailableInclusionsForRoom(
        details.project_type,
        roomName
      );
    } catch (error) {
      console.error("Error fetching available inclusions for room:", error);
      return null;
    }
  };

  const handleDeleteBoq = (boq, inclusionId) => {
    setBoqToDelete({ ...boq, inclusionId });
    setIsDeleteBoqModalOpen(true);
  };

  const handleConfirmDeleteBoq = async (boqId, inclusionId) => {
    try {
      const response = await deleteBOQ(details.id, boqId);
      if (response.success) {
        toast.success("BOQ item deleted successfully");
        loadBudgetData();
        setIsDeleteBoqModalOpen(false);
        setBoqToDelete(null);
      } else {
        toast.error("Failed to delete BOQ item");
      }
    } catch (error) {
      console.error("Error deleting BOQ:", error);
      toast.error("Failed to delete BOQ item");
    }
  };

  const handleCloseDeleteBoqModal = () => {
    setIsDeleteBoqModalOpen(false);
    setBoqToDelete(null);
  };

  const handleDeleteInclusion = (inclusion) => {
    setInclusionToDelete(inclusion);
    setIsDeleteInclusionModalOpen(true);
  };

  const handleConfirmDeleteInclusion = async (
    inclusionIdOrRoomId,
    inclusionName = null
  ) => {
    try {
      const response = await deleteInclusion(
        inclusionName ? null : inclusionIdOrRoomId,
        inclusionName ? inclusionIdOrRoomId : null,
        inclusionName
      );
      if (response.success) {
        toast.success("Inclusion deleted successfully");
        loadBudgetData();
        setIsDeleteInclusionModalOpen(false);
        setInclusionToDelete(null);
      } else {
        toast.error("Failed to delete inclusion");
      }
    } catch (error) {
      console.error("Error deleting inclusion:", error);
      toast.error("Failed to delete inclusion");
    }
  };

  const handleCloseDeleteInclusionModal = () => {
    setIsDeleteInclusionModalOpen(false);
    setInclusionToDelete(null);
  };

  const handleUpdateBoq = async (itemData) => {
    if (!editingBoq || !selectedInclusionForItem) return;

    setIsAddingItem(true);
    try {
      const boqType = itemData.boq_type;
      const unitCostFallback =
        boqType === "Activities" ? editingBoq.manpower_rate : editingBoq.unit_cost;
      const trimmedUnitCost = String(itemData.unit_cost ?? "").trim();
      const unitCostValue =
        trimmedUnitCost !== ""
          ? trimmedUnitCost
          : unitCostFallback != null
          ? String(unitCostFallback)
          : null;

      const updateData = {
        name: itemData.item_name,
        boq_type: boqType,
        subcategory: itemData.subcategory?.label || itemData.subcategory || "",
        supply_type: itemData.supply_apply?.label || itemData.supply_apply,
        specification: itemData.specification,
        lead_time: itemData.lead_time != null ? itemData.lead_time : null,
        ...(boqType === "Activities"
          ? {
              days: itemData.days || null,
              manpower_rate: unitCostValue,
              qty: null,
              material_unit_price: null,
            }
          : {
              material_unit: itemData.material_unit?.value || itemData.material_unit,
              qty: itemData.quantity || null,
              material_unit_price: unitCostValue,
              days: null,
              manpower_rate: null,
            }),
      };

      const response = await updateBOQ(details.id, editingBoq.id, updateData);

      if (response.success) {
        toast.success("BOQ item updated successfully");
        loadBudgetData();
        handleCloseAddItemModal();
      } else {
        toast.error("Failed to update BOQ item");
      }
    } catch (error) {
      console.error("Error updating BOQ:", error);
      toast.error("Failed to update BOQ item");
    } finally {
      setIsAddingItem(false);
    }
  };

  const handleDeleteBoqImage = async (imageId) => {
    try {
      const response = await getApiData(
        `api/project/delete-files/${imageId}`,
        "POST",
        {}
      );
      if (response.success) {
        toast.success("Image deleted successfully");
        setEditingBoq((prev) => ({ ...prev, image: [] }));
        loadBudgetData();
      } else {
        toast.error("Failed to delete image");
      }
    } catch (error) {
      console.error("Error deleting BOQ image:", error);
      toast.error("Failed to delete image");
    }
  };

  const handleUploadBoqImage = async (file) => {
    if (!editingBoq) return;

    try {
      const formData = new FormData();
      formData.append("project_id", editingBoq.id);
      formData.append("type", "budgetItemDetails");
      formData.append("file_1", file);

      const response = await postFormData("api/project/files", formData);

      if (response.success) {
        toast.success("Image uploaded successfully");
        if (response.data && response.data.length > 0) {
          setEditingBoq((prev) => ({
            ...prev,
            image: [{ image: response.data[0].path, id: response.data[0].id }],
          }));
        }
        loadBudgetData();
      } else {
        toast.error("Failed to upload image");
      }
    } catch (error) {
      console.error("Error uploading BOQ image:", error);
      toast.error("Failed to upload image");
    }
  };

  const handleAddInclusion = async (inclusionName, roomId) => {
    setAddingInclusion(inclusionName);
    try {
      const response = await addInclusionToRoom(
        details.id,
        roomId,
        inclusionName
      );

      if (response.success) {
        toast.success(`Successfully added ${inclusionName} to room`);
        loadBudgetData();
      } else {
        toast.error("Failed to add inclusion");
      }
    } catch (error) {
      console.error("Error adding inclusion:", error);
      toast.error("Failed to add inclusion");
    } finally {
      setAddingInclusion(null);
    }
  };

  const handleAddItem = async (itemData) => {
    if (!selectedInclusionForItem) return;

    setIsAddingItem(true);
    try {
      let inclusionId = inclusionIdForModal || selectedInclusionForItem.id;

      if (activeTab === 1 && !inclusionId && selectedRoomForModal) {
        const selectedRoomId =
          selectedRoomForModal.id ||
          selectedRoomForModal.value ||
          selectedRoomForModal.room_id;

        const roomInInclusion = selectedInclusionForItem.rooms?.find(
          (r) => (r.id || r.room_id) === selectedRoomId
        );

        if (roomInInclusion?.inclusion_id) {
          inclusionId = roomInInclusion.inclusion_id;
        } else if (roomInInclusion?.boqs && roomInInclusion.boqs.length > 0) {
          inclusionId =
            roomInInclusion.boqs[0]?.inclusion_id ||
            selectedInclusionForItem.id;
        } else if (selectedInclusionForItem.id) {
          inclusionId = selectedInclusionForItem.id;
        }
      }

      if (!inclusionId || inclusionId === "undefined") {
        if (activeTab === 1) {
          if (!selectedRoomForModal) {
            toast.error("Please select a room first.");
          } else {
            toast.error("Unable to determine inclusion ID. Please try again.");
          }
        } else {
          toast.error("Invalid inclusion. Please try again.");
        }
        setIsAddingItem(false);
        return;
      }

      const itemDataWithRoom = { ...itemData };
      if (activeTab === 0 && selectedRoomForModal) {
        itemDataWithRoom.room_id = selectedRoomForModal.id;
      } else if (activeTab === 1) {
        if (selectedRoomForModal) {
          itemDataWithRoom.room_id =
            selectedRoomForModal.id || selectedRoomForModal.value;
        } else if (itemData.room_id) {
          itemDataWithRoom.room_id =
            itemData.room_id.value || itemData.room_id.id || itemData.room_id;
        }
      }

      const response = await addBOQToInclusion(
        details.id,
        inclusionId,
        itemDataWithRoom
      );

      if (response.success) {
        toast.success(
          `Successfully added item to ${selectedInclusionForItem.name}`
        );
        loadBudgetData();
        handleCloseAddItemModal();
      } else {
        toast.error("Failed to add item");
      }
    } catch (error) {
      console.error("Error adding item:", error);
      toast.error("Failed to add item");
    } finally {
      setIsAddingItem(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box sx={budgetStyles.loadingContainer}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  const summaryCards = boqSummary && boqSummary.totalBoqs > 0 && (
    <Box
      sx={{
        display: "grid",
        // Changed to show 2 cards in one row on mobile with equal sizing
        gridTemplateColumns: {
          xs: "repeat(2, 1fr)",
          sm: "repeat(2, 1fr)",
        },
        gap: 1.5,
        mb: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "12px",
          backgroundColor: "#EFF2F580",
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 2, sm: 3 },
          textAlign: "center",
          minHeight: { xs: "100px", sm: "auto" },
        }}
      >
        <Box
          sx={{
            fontSize: { xs: 18, sm: 28 },
            fontWeight: 700,
            color: "#4c6bf6",
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            "& svg": { width: { xs: 16, sm: 24 }, height: { xs: 16, sm: 24 } },
          }}
        >
          {renderCurrencyIconColored("large")}
          {formattedAmountWithoutAed(boqSummary.totalBoqCost)}
        </Box>
        <Box sx={{ mt: 1, fontSize: { xs: 11, sm: 13 }, fontWeight: 500, color: "#627084" }}>
          Total BOQ Cost
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "12px",
          backgroundColor: "#E7B0081A",
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 2, sm: 3 },
          textAlign: "center",
          minHeight: { xs: "100px", sm: "auto" },
        }}
      >
        <Box sx={{ fontSize: { xs: 18, sm: 22 }, fontWeight: 700, color: "#E7B008" }}>
          {boqSummary.coveredBoqs} / {boqSummary.totalBoqs}
        </Box>
        <Box sx={{ mt: 1, fontSize: { xs: 11, sm: 13 }, fontWeight: 500, color: "#627084" }}>
          Completion
        </Box>
      </Box>
    </Box>
  );

  return (
    <div
      style={{
        ...budgetStyles.container,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Box sx={{ flexShrink: 0 }}>{summaryCards}</Box>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          mb: 2,
          flexShrink: 0,
        }}
      >
        <Tabs value={activeTab} onChange={handleTabChange}>
          <StyledTab
            icon={<IoCubeOutline size={16} />}
            iconPosition="start"
            label="By Room"
          />
          <StyledTab
            icon={<IoSettingsOutline size={16} />}
            iconPosition="start"
            label="By Category"
          />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {activeTab === 0 ? (
          <ByRoomTab
            rooms={rooms}
            projectId={details?.id}
            project={details}
            onOpenAddItemModal={handleOpenAddItemModal}
            onAddInclusion={handleAddInclusion}
            addingInclusion={addingInclusion}
            onEditBoq={handleEditBoq}
            onDeleteBoq={handleDeleteBoq}
            onDeleteInclusion={handleDeleteInclusion}
            onBudgetUpdated={loadBudgetData}
            inlineEditingBoqId={inlineEditingBoqId}
            inlineEditingBoqInclusion={inlineEditingBoqInclusion}
            onSaveInlineEditBoq={handleAfterInlineEditSave}
            onCancelInlineEditBoq={handleCancelInlineEditBoq}
            fetchAvailableInclusionsForRoom={fetchAvailableInclusionsForRoom}
            showCostFields={true}
            isMobile={isMobile}
            isTablet={isTablet}
          />
        ) : (
          <ByCategoryTab
            inclusions={inclusions}
            projectId={details?.id}
            project={details}
            onOpenAddItemModal={handleOpenAddItemModal}
            onEditBoq={handleEditBoq}
            onDeleteBoq={handleDeleteBoq}
            onDeleteInclusion={handleDeleteInclusion}
            onBudgetUpdated={loadBudgetData}
            inlineEditingBoqId={inlineEditingBoqId}
            inlineEditingBoqInclusion={inlineEditingBoqInclusion}
            onSaveInlineEditBoq={handleAfterInlineEditSave}
            onCancelInlineEditBoq={handleCancelInlineEditBoq}
            showCostFields={!hideUnitPrice}
            isMobile={isMobile}
            isTablet={isTablet}
          />
        )}
      </Box>

      <AddItemModal
        isOpen={isAddItemModalOpen}
        onClose={handleCloseAddItemModal}
        inclusionName={selectedInclusionForItem?.name}
        onSubmit={isEditingItem ? handleUpdateBoq : handleAddItem}
        loading={isAddingItem}
        isEditing={isEditingItem}
        editingBoq={editingBoq}
        onDeleteImage={handleDeleteBoqImage}
        onUploadImage={handleUploadBoqImage}
        rooms={
          activeTab === 0
            ? selectedRoomForModal
              ? [selectedRoomForModal]
              : []
            : selectedInclusionForItem?.rooms || []
        }
        selectedRoom={selectedRoomForModal}
        onRoomChange={handleRoomChange}
        project={details}
        roomNameForSubcategories={
          activeTab === 0 ? selectedRoomForModal?.room_name || selectedRoomForModal?.name || "" : ""
        }
      />

      <DeleteBoqModal
        open={isDeleteBoqModalOpen}
        onClose={handleCloseDeleteBoqModal}
        boqData={boqToDelete}
        onDeleteBoq={handleConfirmDeleteBoq}
      />

      <DeleteInclusionModal
        open={isDeleteInclusionModalOpen}
        onClose={handleCloseDeleteInclusionModal}
        inclusionData={inclusionToDelete}
        onDeleteInclusion={handleConfirmDeleteInclusion}
        requiresRoomSelection={activeTab === 1}
      />
    </div>
  );
};

export default Budgets;
